import React, { useEffect, useRef, useState } from 'react';

// Reuso do CSS de filtros do Contac, que já serve vários modais por import (at-61).
import '../../../components/modal/contac/filtro/ModalFiltro.css';
import '../../../components/modal/filtro-usuarios/ModalFiltroUsuarios.css';
import '../../../shared/ui/modal-close-button.css';

export interface FiltrosDePrevisao {
  /** Emissão do pedido (dia puro, `AAAA-MM-DD`). */
  emissaoDe: string;
  emissaoAte: string;
  /** `''` = todas; `sim` = só oficina de terceiro; `nao` = só a interna. */
  oficinaExterna: '' | 'sim' | 'nao';
  /** Período de recebimento — só vale na aba Peças, sub-aba Recebidos. */
  recebidoDe: string;
  recebidoAte: string;
}

export const FILTROS_DE_PREVISAO_PADRAO: FiltrosDePrevisao = {
  emissaoDe: '',
  emissaoAte: '',
  oficinaExterna: '',
  recebidoDe: '',
  recebidoAte: '',
};

interface Props {
  /** Filtros EM VIGOR — o modal abre sempre refletindo o que está aplicado (`BUG-V43`). */
  filtrosAplicados: FiltrosDePrevisao;
  /** O período de recebimento só aparece na aba Peças. */
  comRecebimento: boolean;
  onAplicar: (filtros: FiltrosDePrevisao) => void;
  onFechar: () => void;
}

/**
 * Filtros da Previsão de Peças: estado local aplicado em bloco, montagem condicional pela tela e
 * reabertura fiel ao que está aplicado. Valem para as duas abas (Pedidos e Peças).
 */
const FiltroPrevisaoModal: React.FC<Props> = ({ filtrosAplicados, comRecebimento, onAplicar, onFechar }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [filtros, setFiltros] = useState<FiltrosDePrevisao>(filtrosAplicados);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(evento.target as Node)) onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar]);

  const aplicar = () => {
    onAplicar(filtros);
    onFechar();
  };

  return (
    <div className="modal-filtro-overlay">
      <div className="modal-filtro-content" ref={modalRef}>
        <div className="filtro-modal-card">
          <div className="filtro-modal-header">
            <div>
              <h2 className="filtro-modal-title">Filtros da previsão de peças</h2>
              <p className="filtro-modal-subtitle">Os filtros se combinam com a aba escolhida e com a busca</p>
            </div>
            <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
              &times;
            </button>
          </div>

          <div className="filtro-modal-body">
            <div className="filtro-section">
              <h3 className="filtro-section-title">Emissão do pedido</h3>
              <div className="filtro-field">
                <label htmlFor="filtro-emissao-de">De</label>
                <input
                  id="filtro-emissao-de"
                  type="date"
                  className="premium-text-input"
                  value={filtros.emissaoDe}
                  max={filtros.emissaoAte || undefined}
                  onChange={(e) => setFiltros((atual) => ({ ...atual, emissaoDe: e.target.value }))}
                />
              </div>
              <div className="filtro-field">
                <label htmlFor="filtro-emissao-ate">Até</label>
                <input
                  id="filtro-emissao-ate"
                  type="date"
                  className="premium-text-input"
                  value={filtros.emissaoAte}
                  min={filtros.emissaoDe || undefined}
                  onChange={(e) => setFiltros((atual) => ({ ...atual, emissaoAte: e.target.value }))}
                />
              </div>
            </div>

            <div className="filtro-section">
              <h3 className="filtro-section-title">Oficina</h3>
              <div className="filtro-field">
                <label htmlFor="filtro-oficina-externa">Destino</label>
                <div className="select-wrapper">
                  <select
                    id="filtro-oficina-externa"
                    value={filtros.oficinaExterna}
                    onChange={(e) =>
                      setFiltros((atual) => ({ ...atual, oficinaExterna: e.target.value as FiltrosDePrevisao['oficinaExterna'] }))
                    }
                  >
                    <option value="">Todas as oficinas</option>
                    <option value="sim">Só oficinas externas</option>
                    <option value="nao">Só a oficina da Atual</option>
                  </select>
                </div>
                <p className="filtro-field-hint">
                  A oficina vem da requisição que originou o pedido. Pedido sem requisição não tem oficina.
                </p>
              </div>
            </div>

            {comRecebimento && (
              <div className="filtro-section">
                <h3 className="filtro-section-title">Período de recebimento</h3>
                <div className="filtro-field">
                  <label htmlFor="filtro-recebido-de">Recebido de</label>
                  <input
                    id="filtro-recebido-de"
                    type="date"
                    className="premium-text-input"
                    value={filtros.recebidoDe}
                    max={filtros.recebidoAte || undefined}
                    onChange={(e) => setFiltros((atual) => ({ ...atual, recebidoDe: e.target.value }))}
                  />
                </div>
                <div className="filtro-field">
                  <label htmlFor="filtro-recebido-ate">Até</label>
                  <input
                    id="filtro-recebido-ate"
                    type="date"
                    className="premium-text-input"
                    value={filtros.recebidoAte}
                    min={filtros.recebidoDe || undefined}
                    onChange={(e) => setFiltros((atual) => ({ ...atual, recebidoAte: e.target.value }))}
                  />
                  <p className="filtro-field-hint">Vale só na sub-aba Recebidos — as outras são peças que ainda não chegaram.</p>
                </div>
              </div>
            )}
          </div>

          <div className="filtro-modal-footer">
            <button
              type="button"
              className="filtro-btn-secondary filtro-btn-limpar"
              onClick={() => setFiltros(FILTROS_DE_PREVISAO_PADRAO)}
            >
              Limpar filtros
            </button>
            <button type="button" className="filtro-btn-secondary" onClick={onFechar}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={aplicar}>
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltroPrevisaoModal;
